import { memo, useCallback } from 'react'

import { createColumnHelper } from '@tanstack/react-table'

import { Badge, Box, Tooltip } from '@chakra-ui/react'

import { AddressSearch } from '@/components/ui'

import { CSV_UploadStatus, ParsedFacilityI } from './csvParser'

const columnHelper = createColumnHelper<ParsedFacilityI>()

interface AddressI {
  value: string
  latitude?: number
  longitude?: number
}

interface CreateColumnsOptionsI {
  onUpdateAddress?: (rowIndex: number, address: AddressI) => void
}

interface AddressCellIProps {
  rowIndex: number
  currentAddress: string
  onUpdateAddress?: (rowIndex: number, address: AddressI) => void
}

const AddressCell = memo(function AddressCell({
  rowIndex,
  currentAddress,
  onUpdateAddress,
}: AddressCellIProps) {
  const handleSelect = useCallback(
    (address: AddressI | null) => {
      if (address && onUpdateAddress) onUpdateAddress(rowIndex, address)
    },
    [rowIndex, onUpdateAddress]
  )
  return (
    <Box minW='300px'>
      <AddressSearch
        defaultValue={currentAddress}
        onSelectAddress={handleSelect}
      />
    </Box>
  )
})

const getBadgeColor = (status: CSV_UploadStatus) => {
  switch (status) {
    case CSV_UploadStatus.Success:
      return 'incident.resolved.100'
    case CSV_UploadStatus.Failed:
      return 'incident.active.100'
    default:
      return '#888888'
  }
}

export const createColumns = ({ onUpdateAddress }: CreateColumnsOptionsI) => [
  columnHelper.accessor('uploadStatus', {
    header: 'Upload Status',
    cell: (info) => {
      const status = info.getValue() || CSV_UploadStatus.Pending
      const error = info.row.original.uploadError

      const badge = (
        <Badge bgColor={getBadgeColor(status)} color='#fff' px='2' rounded='md'>
          {status}
        </Badge>
      )

      if (status === CSV_UploadStatus.Failed && error) {
        return (
          <Tooltip label={error} placement='top'>
            {badge}
          </Tooltip>
        )
      }
      return badge
    },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('shortName', {
    header: 'Short Name',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('address', {
    header: 'Address',
    cell: (info) => (
      <AddressCell
        currentAddress={info.getValue()}
        onUpdateAddress={onUpdateAddress}
        rowIndex={info.row.index}
      />
    ),
  }),
  columnHelper.accessor('lat', {
    header: 'Latitude',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('lon', {
    header: 'Longitude',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('timezone', {
    header: 'Timezone',
    cell: (info) => info.getValue(),
  }),
]
